import { Link } from "react-router-dom";
import {
  Apple,
  ArrowRight,
  BadgeCheck,
  Bell,
  Calendar,
  CalendarCheck,
  CalendarClock,
  ClipboardCheck,
  ClipboardList,
  CreditCard,
  Crown,
  Flame,
  ListChecks,
  MapPin,
  Megaphone,
  Play,
  Repeat,
  ShieldCheck,
  Smartphone,
  Star,
  Store,
  Tags,
  TrendingUp,
  Users,
  Wrench,
} from "lucide-react";
import { Eyebrow, MaskLine, Reveal, SectionHeading } from "@/components/Reveal";
import { CraftsiLogo } from "@/components/Logo";
import PhoneMockup from "@/components/PhoneMockup";
import CtaBand from "@/components/CtaBand";
import { useContent } from "@/context/ContentContext";

const FEATURES = [
  { Icon: MapPin, title: "Discover Local Services", text: "Find trusted businesses and professionals around you, across categories." },
  { Icon: CalendarCheck, title: "Book & Request Services", text: "Book instantly or raise a request and let providers respond." },
  { Icon: Store, title: "Business Profiles", text: "Rich profiles with services, photos, pricing and customer feedback." },
  { Icon: ClipboardList, title: "Vendor Management", text: "Tools for vendors to run services, staff, schedules and operations." },
  { Icon: Users, title: "Customer Management", text: "Vendors can track customers, history and repeat business." },
  { Icon: ListChecks, title: "Service Listings", text: "Structured listings that make every offering easy to explore." },
  { Icon: CalendarCheck, title: "Appointments & Job Requests", text: "Scheduled appointments and on-demand job requests in one flow." },
  { Icon: CreditCard, title: "Payments", text: "Simple, integrated payment experiences for services booked." },
  { Icon: Bell, title: "Notifications", text: "Real-time updates on bookings, requests and messages." },
  { Icon: Megaphone, title: "Promotions & Sponsored Visibility", text: "Tools for businesses to promote services and reach more customers." },
];

const Craftsi = () => {
  const content = useContent();
  return (
  <>
    <section data-testid="craftsi-hero" className="overflow-hidden bg-mist pb-24 pt-40 sm:pt-48">
      <div className="mx-auto grid max-w-7xl items-center gap-20 px-5 sm:px-8 lg:grid-cols-2">
        <div>
          <Reveal>
            <CraftsiLogo large />
          </Reveal>
          <h1 className="mt-8 font-heading text-4xl font-extrabold leading-[1.06] tracking-tight text-navy sm:text-5xl lg:text-6xl">
            <MaskLine delay={0.15}>Your Local Marketplace</MaskLine>
            <MaskLine delay={0.3}>for Every Need<span className="text-gold">.</span></MaskLine>
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-fog md:text-lg">
              {content.craftsi_intro ||
                "Craftsi is the flagship digital platform of Sunkev Enterprises Private Limited, designed to connect customers with trusted local businesses and service providers. From home services and beauty to healthcare, education, automotive services, household services and other local needs — Craftsi makes discovering and accessing services simple and convenient."}
            </p>
          </Reveal>
          <Reveal delay={0.6}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/contact?subject=Craftsi%20Enquiry"
                data-testid="craftsi-contact-button"
                className="group inline-flex items-center gap-2 rounded-full bg-navy px-8 py-4 font-heading text-sm font-bold uppercase tracking-widest text-white transition-all duration-300 hover:bg-navy-800 hover:shadow-xl hover:shadow-navy/25"
              >
                Partner With Craftsi
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
        <div className="relative mx-auto flex items-end justify-center lg:justify-end">
          <Reveal className="relative z-10 -rotate-2" delay={0.2}>
            <PhoneMockup variant="customer" />
          </Reveal>
          <Reveal className="relative z-0 -ml-16 hidden translate-y-10 rotate-3 sm:block" delay={0.35}>
            <PhoneMockup variant="vendor" />
          </Reveal>
        </div>
      </div>
    </section>

    <section data-testid="craftsi-journey" className="cta-glow py-20">
      <div className="mx-auto max-w-7xl space-y-9 px-5 sm:px-8">
        {[
          { label: "Customers", steps: ["Find", "Compare", "Book or Request", "Pay", "Experience", "Review"] },
          { label: "Vendors", steps: ["List", "Manage", "Receive Customers", "Get Paid", "Grow"] },
          { label: "Sunkev Enterprises", steps: ["Verify", "Manage", "Moderate", "Promote", "Analyze", "Monetize"] },
        ].map((row, i) => (
          <Reveal key={row.label} delay={i * 0.1}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-8">
              <span className="w-44 shrink-0 text-[10px] font-bold uppercase tracking-[0.28em] text-gold">
                {row.label}
              </span>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                {row.steps.map((s, j) => (
                  <span key={s} className="flex items-center gap-3">
                    <span className="font-heading text-sm font-bold text-white sm:text-base">{s}</span>
                    {j < row.steps.length - 1 && (
                      <ArrowRight className="h-3.5 w-3.5 text-gold/70" />
                    )}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <section className="py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading
          eyebrow="Platform Capabilities"
          title="One Platform. Multiple Possibilities."
          text="A complete ecosystem for customers to find services and for businesses to run and grow their operations."
        />
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={(i % 5) * 0.06}>
              <div className="group h-full rounded-2xl border border-gray-100 bg-white p-6 transition-all duration-500 hover:-translate-y-1 hover:border-royal/30 hover:shadow-lg hover:shadow-navy/10">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-mist transition-colors duration-500 group-hover:bg-navy">
                  <f.Icon className="h-5 w-5 text-royal transition-colors duration-500 group-hover:text-gold" strokeWidth={1.75} />
                </span>
                <h3 className="mt-5 font-heading text-sm font-bold leading-snug text-ink">{f.title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-fog">{f.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section data-testid="craftsi-engines" className="bg-mist py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading
          eyebrow="Two Service Engines"
          title="Appointments and on-demand jobs — both built in."
          text="Not every business works with fixed time slots. Craftsi supports the two ways local services actually work — and lets every business run either or both."
        />
        <div className="mt-14 grid gap-6 lg:grid-cols-2">
          {[
            {
              Icon: CalendarClock,
              tag: "Appointment Engine",
              title: "The customer picks a time slot.",
              text: "For salons, spas, clinics, fitness and training businesses — with calendars, available time slots, service durations, staff schedules, working hours, reminders, rescheduling and cancellations.",
              chips: ["Calendar", "Time Slots", "Staff Schedules", "Reminders", "Rescheduling"],
            },
            {
              Icon: Wrench,
              tag: "Job / Task Engine",
              title: "The customer raises a request.",
              text: "For electricians, plumbers, AC repair, cleaning, movers, drivers and other on-demand services — vendors accept or reject, assign staff, share an estimated arrival time and update live job status.",
              chips: ["Accept / Reject", "Assign Staff", "ETA Updates", "Live Status", "Job Completion"],
            },
          ].map((e, i) => (
            <Reveal key={e.tag} delay={i * 0.12}>
              <div className="h-full rounded-3xl border border-gray-100 bg-white p-9 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-navy/10">
                <div className="flex items-center gap-4">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-navy">
                    <e.Icon className="h-5 w-5 text-gold" />
                  </span>
                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-gold-dark">
                      {e.tag}
                    </span>
                    <h3 className="font-heading text-2xl font-bold text-ink">{e.title}</h3>
                  </div>
                </div>
                <p className="mt-6 text-sm leading-relaxed text-fog">{e.text}</p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {e.chips.map((c) => (
                    <span
                      key={c}
                      className="rounded-full bg-mist px-3.5 py-1.5 text-[11px] font-semibold text-navy"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-6 flex flex-col items-start gap-5 rounded-3xl border border-gold/25 bg-gold/5 p-8 sm:flex-row sm:items-center">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-navy">
              <Repeat className="h-5 w-5 text-gold" />
            </span>
            <div>
              <h3 className="font-heading text-xl font-bold text-ink">Hybrid businesses run both.</h3>
              <p className="mt-2 max-w-3xl text-sm leading-relaxed text-fog">
                A salon can take appointments at the store and requests for at-home service.
                Every vendor on Craftsi can operate appointment-based, request-based — or both.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <section data-testid="craftsi-categories" className="py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading
          eyebrow="Every Local Need"
          title="One marketplace, every category."
          text="From salons and spas to electricians, clinics, pet care and packers & movers. Categories and subcategories are managed by Craftsi administrators and can grow without app changes."
        />
        <div className="mt-12 flex flex-wrap gap-3">
          {[
            "Salons & Beauty",
            "Fitness & Sports",
            "Household & Care",
            "Home Repair & Maintenance",
            "Home Construction",
            "Clinics & Healthcare",
            "Laundry & Cleaning",
            "Dairy & Meat",
            "Automotive Services",
            "Education & Training",
            "Pet Care",
            "Property Rentals",
            "Religious & Cultural",
            "Tailoring",
            "Packers & Movers",
            "Drivers",
            "And more…",
          ].map((c, i) => (
            <Reveal key={c} delay={(i % 8) * 0.04}>
              <span
                data-testid="category-chip"
                className="inline-block rounded-full border border-navy/10 bg-white px-5 py-2.5 font-heading text-sm font-semibold text-navy shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-gold hover:text-gold-dark"
              >
                {c}
              </span>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-mist py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading
          eyebrow="Two Apps. One Ecosystem."
          title="Built for customers and businesses alike."
        />
        <div className="mt-14 grid gap-8 lg:grid-cols-2">
          {[
            {
              Icon: Smartphone,
              tag: "Customer App",
              title: "Everything you need, around you.",
              points: [
                "Discover local businesses across every category",
                "Explore services, pricing and business profiles",
                "Request or book services in a few taps",
                "Track bookings and manage all activity in one place",
              ],
            },
            {
              Icon: Store,
              tag: "Vendor App",
              title: "Run and grow your business.",
              points: [
                "Manage your business profile and service listings",
                "Receive and manage bookings and job requests",
                "Build customer relationships and repeat business",
                "Promote services with sponsored visibility",
              ],
            },
          ].map((c, i) => (
            <Reveal key={c.tag} delay={i * 0.12}>
              <div className="h-full rounded-3xl border border-gray-100 bg-white p-10 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-navy/10">
                <div className="flex items-center gap-4">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-navy">
                    <c.Icon className="h-5 w-5 text-gold" />
                  </span>
                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-gold-dark">
                      {c.tag}
                    </span>
                    <h3 className="font-heading text-2xl font-bold text-ink">{c.title}</h3>
                  </div>
                </div>
                <ul className="mt-8 space-y-4">
                  {c.points.map((p) => (
                    <li key={p} className="flex items-start gap-3 text-sm leading-relaxed text-fog">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="mt-12 text-center text-sm text-fog">
            Craftsi is a product of{" "}
            <span className="font-semibold text-navy">Sunkev Enterprises Private Limited</span>.
          </p>
        </Reveal>
      </div>
    </section>

    <section data-testid="craftsi-vendor-platform" className="py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading
          eyebrow="Vendor Platform"
          title="Not just a marketplace — a complete business platform."
          text="The Craftsi Vendor App gives local businesses the tools usually reserved for large companies — a modular dashboard that adapts to how each business works."
        />
        <div className="mt-14 grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
          {[
            { Icon: CalendarCheck, title: "Appointments" },
            { Icon: ClipboardList, title: "Jobs & Requests" },
            { Icon: ListChecks, title: "Services & Pricing" },
            { Icon: Users, title: "Customers" },
            { Icon: BadgeCheck, title: "Staff" },
            { Icon: Calendar, title: "Calendar" },
            { Icon: CreditCard, title: "Checkout & Payments" },
            { Icon: Crown, title: "Memberships" },
            { Icon: Megaphone, title: "Promotions" },
            { Icon: Store, title: "Business Profile" },
          ].map((m, i) => (
            <Reveal key={m.title} delay={(i % 5) * 0.06}>
              <div className="group flex h-full flex-col rounded-2xl border border-gray-100 bg-white p-6 transition-all duration-500 hover:-translate-y-1 hover:border-royal/30 hover:shadow-lg hover:shadow-navy/10">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-mist transition-colors duration-500 group-hover:bg-navy">
                  <m.Icon className="h-5 w-5 text-royal transition-colors duration-500 group-hover:text-gold" strokeWidth={1.75} />
                </span>
                <h3 className="mt-5 font-heading text-sm font-bold leading-snug text-ink">{m.title}</h3>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-8 grid gap-6 lg:grid-cols-2">
          {[
            {
              Icon: Tags,
              title: "Fixed-price services",
              text: "Customers see the price upfront and book or request directly — like a haircut at a listed price.",
            },
            {
              Icon: ClipboardCheck,
              title: "Quotation-based services",
              text: "For work like home renovation, customers request a quote and the vendor shares an estimate. Craftsi administrators control which categories may use quotations.",
            },
          ].map((p, i) => (
            <Reveal key={p.title} delay={i * 0.1}>
              <div className="flex h-full items-start gap-4 rounded-3xl border border-gray-100 bg-mist p-7">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-navy">
                  <p.Icon className="h-5 w-5 text-gold" />
                </span>
                <div>
                  <h3 className="font-heading text-lg font-bold text-ink">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-fog">{p.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section data-testid="craftsi-admin" className="cta-glow py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <Eyebrow>Platform Governance</Eyebrow>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-5 max-w-2xl font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
            Operated and governed by Sunkev Enterprises.
          </h2>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/70 md:text-lg">
            A dedicated Craftsi admin platform keeps the marketplace trusted, organised and
            fair for everyone on it.
          </p>
        </Reveal>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { Icon: ShieldCheck, title: "Vendor Management", text: "Approvals, verification, documents, performance and status — every vendor is reviewed before going live." },
            { Icon: Tags, title: "Category Management", text: "Categories, subcategories, service types, icons and ordering are centrally managed and scalable." },
            { Icon: ClipboardCheck, title: "Service Approval", text: "Quotation-based, sensitive and healthcare-related services are reviewed before they appear to customers." },
            { Icon: Star, title: "Review Moderation", text: "Ratings and reviews are moderated so customers can trust what they read." },
          ].map((a, i) => (
            <Reveal key={a.title} delay={i * 0.08}>
              <div className="h-full rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur-sm transition-all duration-500 hover:-translate-y-1 hover:border-gold/40">
                <a.Icon className="h-8 w-8 text-gold" strokeWidth={1.5} />
                <h3 className="mt-6 font-heading text-lg font-bold text-white">{a.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/60">{a.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section data-testid="craftsi-subscriptions" className="bg-mist py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading
          eyebrow="Growth & Visibility"
          title="Built to grow — for vendors and the platform."
          text="Vendor subscription plans and sponsored visibility help businesses reach more customers while keeping the marketplace sustainable."
        />
        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {[
            { Icon: Store, name: "Basic", text: "For small and new vendors getting started on Craftsi." },
            { Icon: TrendingUp, name: "Professional", text: "For growing businesses that need more services, staff tools and customer management." },
            { Icon: Crown, name: "Premium", text: "For businesses that want greater visibility, analytics and advanced capabilities." },
          ].map((p, i) => (
            <Reveal key={p.name} delay={i * 0.1}>
              <div className="h-full rounded-3xl border border-gray-100 bg-white p-9 shadow-sm transition-all duration-500 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-navy/10">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-navy">
                  <p.Icon className="h-5 w-5 text-gold" />
                </span>
                <h3 className="mt-6 font-heading text-2xl font-bold text-ink">{p.name}</h3>
                <p className="mt-3 text-sm leading-relaxed text-fog">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <p className="mt-6 text-center text-xs text-fog">
            Plan pricing and feature limits are configured by the Craftsi admin platform.
          </p>
        </Reveal>
        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          {[
            { Icon: Megaphone, name: "Sponsored Listings", text: "Vendors can appear prominently within relevant categories." },
            { Icon: Flame, name: "Hot Picks", text: "Selected businesses and services receive extra visibility." },
            { Icon: CalendarClock, name: "Campaigns", text: "Themed promotions like weekend deals on the customer home screen, controlled by the admin." },
          ].map((s, i) => (
            <Reveal key={s.name} delay={i * 0.1}>
              <div className="flex h-full items-start gap-4 rounded-3xl border border-gold/20 bg-white p-7">
                <s.Icon className="h-6 w-6 shrink-0 text-gold-dark" strokeWidth={1.75} />
                <div>
                  <h3 className="font-heading text-base font-bold text-ink">{s.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-fog">{s.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="mt-6 text-center text-xs text-fog">
            Sponsored content is always clearly distinguished from organic results.
          </p>
        </Reveal>
      </div>
    </section>

    <section data-testid="craftsi-download" className="py-24 sm:py-32">
      <div className="mx-auto max-w-4xl px-5 text-center sm:px-8">
        <Reveal>
          <Eyebrow className="justify-center">Mobile Apps</Eyebrow>
          <h2 className="mt-5 font-heading text-3xl font-bold tracking-tight text-ink sm:text-4xl">
            Craftsi in your pocket.
          </h2>
        </Reveal>
        <Reveal delay={0.12}>
          <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-fog md:text-lg">
            The Craftsi Customer App and Vendor App are coming soon to Google Play and the
            App Store. Stay tuned.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-10 grid gap-4 sm:grid-cols-2">
            {[
              { Icon: Play, store: "Google Play", app: "Customer App", key: "store_play_customer" },
              { Icon: Play, store: "Google Play", app: "Vendor App", key: "store_play_vendor" },
              { Icon: Apple, store: "App Store", app: "Customer App", key: "store_ios_customer" },
              { Icon: Apple, store: "App Store", app: "Vendor App", key: "store_ios_vendor" },
            ].map(({ Icon, store, app, key }) => {
              const link = content[key];
              const inner = (
                <>
                  <Icon className="h-8 w-8 shrink-0 text-white" strokeWidth={1.5} />
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-white/60">
                      {link ? "Get it on" : "Coming soon on"} {store}
                    </p>
                    <p className="font-heading text-lg font-bold text-white">Craftsi {app}</p>
                  </div>
                  {!link && (
                    <span className="absolute -right-9 top-2 rotate-45 bg-gold px-10 py-0.5 text-[9px] font-bold uppercase tracking-widest text-navy-900">
                      Soon
                    </span>
                  )}
                </>
              );
              const cls = `relative flex items-center gap-4 rounded-2xl bg-ink px-7 py-4 text-left shadow-xl shadow-navy/15 transition-all duration-300 ${
                link ? "hover:-translate-y-1 hover:shadow-2xl" : "overflow-hidden"
              }`;
              return link ? (
                <a
                  key={key}
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`store-badge-${key}`}
                  className={cls}
                >
                  {inner}
                </a>
              ) : (
                <div key={key} data-testid={`store-badge-${key}`} className={cls}>
                  {inner}
                </div>
              );
            })}
          </div>
          <Reveal delay={0.25}>
            <p className="mt-6 text-xs text-fog">
              Store links are managed by Sunkev Enterprises from the admin panel.
            </p>
          </Reveal>
        </Reveal>
      </div>
    </section>

    <CtaBand />
  </>
  );
};

export default Craftsi;
